/**
 * Resource definitions and route-path constants for the portal.
 *
 * `RESOURCES` is the Refine resource array (consumed by `App.tsx`, Frontend
 * Integration). Resource `name` === the API/Refine kebab path (IMPLEMENTATION
 * CONTRACT §5). The list/show/create/edit paths follow the frontend.spec §7
 * convention. Read-only resources (`events`, `rule-runs`, `agent-runs`) expose
 * only list/show — their writes are the SPECIAL ENDPOINTS (§7) and are not
 * generic CRUD.
 */

import type { ResourceProps } from '@refinedev/core';

/** Custom (non-resource) route paths wired in `App.tsx` per CONTRACT §15.4. */
export const ROUTES = {
  home: '/',
  dashboard: '/dashboard',
  operations: '/operations',
  okrDashboard: '/okr-dashboard',
  skillsTree: '/skills/tree',
  processGraph: '/processes/:id/graph',
  agentRunTrace: '/agent-runs/:id/trace',
  docs: '/docs',
  docDetail: '/docs/:slug',
  login: '/login',
  // v0.2 additions (CONTRACT_V2 §3.4 / §13). The Package Graph is a custom
  // visualization route (owned by D2); the context atlas/detail and the
  // type-scoped package views are plain resource routes registered below.
  contextAtlas: '/contexts',
  contextDetail: '/contexts/:id',
  packageGraph: '/package-graph',
  bets: '/bets',
  frames: '/frames',
  principles: '/principles',
  actionCards: '/action-cards',
  contextControlRoom: '/contexts/:id/control-room',
  decisionRoom: '/decisions/:id/room',
  // S13 (Wave 4a) human surfaces: the Approval Center, the Runtime Trace Viewer
  // (keyed by agent run), and the Context Packet Inspector (keyed by context +
  // ?lens_id). Custom routes wired in App.tsx; not Refine CRUD resources.
  approvalCenter: '/approvals',
  traceViewer: '/agent-runs/:id/trace-viewer',
  packetInspector: '/contexts/:id/packet',
  // S14 (Wave 4b) read-projection surfaces. The id-keyed drills nest under
  // their owning resource block in App.tsx; the list-first ones are top-level.
  projectControlTower: '/projects/:id/control-tower',
  contextExplorer: '/contexts/:id/explorer',
  attentionQueue: '/attention-queue',
  strategyMap: '/contexts/:id/strategy-map',
  skillWorkspace: '/skills/:id/workspace',
  contourBoard: '/processes/:id/board',
  accessLens: '/access-lens/:actorId',
  actorPassport: '/actors/:id/passport',
  lensBuilder: '/lens-builder',
  lensBuilderDetail: '/lens-builder/:id',
  packageCatalog: '/package-catalog',
  // S05/S18 (Wave 4c) executive + knowledge surfaces. The Executive Cockpit is
  // the founder/top-management portfolio landing; the Knowledge Assistant is the
  // access-filtered retrieval workbench. Both are top-level custom routes (not
  // Refine CRUD resources), wired in App.tsx.
  executiveCockpit: '/executive',
  knowledgeAssistant: '/knowledge/assistant',
} as const;

/**
 * Build the standard Refine CRUD route set for a resource path. Read-only
 * resources omit `create`/`edit` by passing `crud: false`.
 */
function crudRoutes(
  name: string,
  label: string,
  options: {
    crud: boolean;
    /** When `false`, register create+show+list but NO edit (append-only). */
    edit?: boolean;
    meta?: Record<string, unknown>;
  } = { crud: true },
): ResourceProps {
  const base: ResourceProps = {
    name,
    list: `/${name}`,
    show: `/${name}/:id`,
    meta: { label, ...options.meta },
  };
  if (options.crud) {
    base.create = `/${name}/create`;
    if (options.edit !== false) {
      base.edit = `/${name}/:id/edit`;
    }
  }
  return base;
}

/**
 * The full Refine resource registry. Order drives the default sidebar order.
 * Every `name` is the kebab API path so the data provider maps resource → path
 * 1:1 (CONTRACT §5).
 */
export const RESOURCES: ResourceProps[] = [
  crudRoutes('projects', 'Projects'),
  crudRoutes('companies', 'Companies'),
  crudRoutes('artifacts', 'Artifacts'),
  crudRoutes('tasks', 'Tasks'),
  crudRoutes('documents', 'Documents'),
  crudRoutes('evidence', 'Evidence'),
  crudRoutes('decisions', 'Decisions'),
  crudRoutes('objectives', 'Objectives'),
  crudRoutes('key-results', 'Key Results'),
  crudRoutes('metrics', 'Metrics'),
  crudRoutes('measurements', 'Measurements'),
  crudRoutes('skills', 'Skills'),
  crudRoutes('processes', 'Processes'),
  crudRoutes('phases', 'Phases'),
  crudRoutes('rules', 'Rules'),
  crudRoutes('events', 'Events', { crud: false }),
  crudRoutes('rule-runs', 'Rule Runs', { crud: false }),
  crudRoutes('agent-runs', 'Agent Runs', { crud: false }),
  // Runtime/observability [PROPOSED] modules (frontend-entity-coverage §2.3).
  // Read-only list/show; their writes (event-source enable/disable, transition
  // approve/reject) are SPECIAL ENDPOINTS, not generic CRUD. They render a
  // labeled-empty teaser until their backing endpoints ship.
  crudRoutes('event-sources', 'Event Sources', { crud: false }),
  crudRoutes('effect-runs', 'Effect Runs', { crud: false }),
  crudRoutes('transition-proposals', 'Transition Proposals', { crud: false }),
  crudRoutes('contour-instances', 'Contour Instances', { crud: false }),
  crudRoutes('action-cards', 'Action Cards', { crud: false }),
  crudRoutes('users', 'Users'),
  crudRoutes('roles', 'Roles', { crud: false }),
  // --- v0.2: Context Atlas / Signals / Package Graph / Positions / Claims /
  // Timers (CONTRACT_V2 §3.4). Names are the kebab API paths 1:1. `package-links`
  // is append-only (create+show+list, no edit). `bets`/`frames`/`principles` are
  // type-scoped facades over the `packages` store: they are NOT separate API
  // resources, so they are wired as custom routes in App.tsx (owned by Frontend
  // Integration) rather than registered here as Refine resources.
  crudRoutes('contexts', 'Contexts'),
  crudRoutes('context-bindings', 'Context Bindings'),
  crudRoutes('signals', 'Signals'),
  crudRoutes('packages', 'Packages'),
  crudRoutes('package-links', 'Package Links', { crud: true, edit: false }),
  crudRoutes('claims', 'Claims'),
  crudRoutes('artifact-positions', 'Artifact Positions'),
  crudRoutes('timers', 'Timers'),
];

/** Set of resource path names that expose only list/show (no generic writes). */
export const READ_ONLY_RESOURCES: ReadonlySet<string> = new Set([
  'events',
  'rule-runs',
  'agent-runs',
  'event-sources',
  'effect-runs',
  'transition-proposals',
  'contour-instances',
  'action-cards',
]);

/** All resource path names, for quick membership checks. */
export const RESOURCE_NAMES: readonly string[] = RESOURCES.map((r) => r.name);
